/**
 * Brand Details & Management Page
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const emptyHours = () =>
    Object.fromEntries(DAYS.map(day => [day, { open: '', close: '', closed: false }]));

function Brand() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { getAccessTokenSilently } = useAuth0();

    const [brand, setBrand] = useState(null);
    const [analytics, setAnalytics] = useState(null);
    const [inventoryCategories, setInventoryCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);

    // Form state
    const [formData, setFormData] = useState({
        name: '',
        industry: '',
        phone: '',
        email: '',
        website: '',
        description: '',
        address: {
            street: '',
            city: '',
            state: '',
            zipCode: '',
            country: 'USA'
        },
        twilioNumber: '',
        hours: emptyHours(),
        aiPersonality: {
            tone: 'professional',
            greeting: '',
            signature: ''
        },
        voice: {
            enabled: false,
            greeting: '',
            ttsProvider: 'Google',
            voiceId: 'en-US-Journey-O',
            languages: ['en-US'],
            interruptible: 'dtmf',
            fallbackNumber: ''
        },
        knowledgeBase: []
    });

    useEffect(() => {
        fetchBrandData();
    }, [id]);

    const fetchBrandData = async () => {
        try {
            setLoading(true);
            const token = await getAccessTokenSilently();
            const headers = { Authorization: `Bearer ${token}` };

            const [brandRes, analyticsRes, inventoryRes] = await Promise.all([
                axios.get(`/api/admin/brands/${id}`, { headers }),
                axios.get(`/api/admin/brands/${id}/analytics`, { headers }),
                axios.get(`/api/admin/brands/${id}/inventory`, { headers })
            ]);

            const brandData = brandRes.data.brand;
            setBrand(brandData);
            setAnalytics(analyticsRes.data.analytics);
            setInventoryCategories(inventoryRes.data.inventory || []);

            // Populate form
            setFormData({
                name: brandData.name || '',
                industry: brandData.industry || '',
                phone: brandData.phone || '',
                email: brandData.email || '',
                website: brandData.website || '',
                description: brandData.description || '',
                address: {
                    street: brandData.address?.street || '',
                    city: brandData.address?.city || '',
                    state: brandData.address?.state || '',
                    zipCode: brandData.address?.zipCode || '',
                    country: brandData.address?.country || 'USA'
                },
                twilioNumber: brandData.twilioNumber || '',
                hours: Object.fromEntries(DAYS.map(day => [day, {
                    open: brandData.hours?.[day]?.open || '',
                    close: brandData.hours?.[day]?.close || '',
                    closed: brandData.hours?.[day]?.closed ?? false
                }])),
                aiPersonality: {
                    tone: brandData.aiPersonality?.tone || 'professional',
                    greeting: brandData.aiPersonality?.greeting || '',
                    signature: brandData.aiPersonality?.signature || ''
                },
                voice: {
                    enabled: brandData.voice?.enabled ?? false,
                    greeting: brandData.voice?.greeting || '',
                    ttsProvider: brandData.voice?.ttsProvider || 'Google',
                    voiceId: brandData.voice?.voiceId || 'en-US-Journey-O',
                    languages: brandData.voice?.languages || ['en-US'],
                    interruptible: brandData.voice?.interruptible || 'dtmf',
                    fallbackNumber: brandData.voice?.fallbackNumber || ''
                },
                knowledgeBase: brandData.knowledgeBase || []
            });

        } catch (err) {
            console.error('Error fetching brand data:', err);
            setError('Failed to load brand data.');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            setFormData(prev => ({
                ...prev,
                [parent]: {
                    ...prev[parent],
                    [child]: value
                }
            }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleHoursChange = (day, field, value) => {
        setFormData(prev => ({
            ...prev,
            hours: {
                ...prev.hours,
                [day]: { ...prev.hours[day], [field]: value }
            }
        }));
    };

    const handleVoiceEnabledChange = (e) => {
        setFormData(prev => ({ ...prev, voice: { ...prev.voice, enabled: e.target.checked } }));
    };

    const handleBilingualChange = (e) => {
        const languages = e.target.checked ? ['en-US', 'fr-CA'] : ['en-US'];
        setFormData(prev => ({ ...prev, voice: { ...prev.voice, languages } }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const E164_RE = /^\+[1-9]\d{1,14}$/;
            if (formData.twilioNumber && !E164_RE.test(formData.twilioNumber)) {
                alert('Twilio Number must be E.164 format (e.g. +19029030090) or left blank.');
                return;
            }
            if (formData.voice.fallbackNumber && !E164_RE.test(formData.voice.fallbackNumber)) {
                alert('Fallback Number must be E.164 format (e.g. +19029030090) or left blank.');
                return;
            }
            setSaving(true);
            const token = await getAccessTokenSilently();
            await axios.put(`/api/admin/brands/${id}`, formData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            alert('Brand updated successfully!');
            fetchBrandData(); // Refresh
        } catch (err) {
            console.error('Error saving brand:', err);
            alert('Failed to update brand.');
        } finally {
            setSaving(false);
        }
    };

    const handleKBChange = (index, field, value) => {
        const newKB = [...formData.knowledgeBase];
        newKB[index] = { ...newKB[index], [field]: value };
        setFormData(prev => ({ ...prev, knowledgeBase: newKB }));
    };

    const addKBItem = () => {
        setFormData(prev => ({
            ...prev,
            knowledgeBase: [...prev.knowledgeBase, { question: '', answer: '' }]
        }));
    };

    const removeKBItem = (index) => {
        if (!window.confirm('Are you sure you want to remove this question?')) return;
        const newKB = formData.knowledgeBase.filter((_, i) => i !== index);
        setFormData(prev => ({ ...prev, knowledgeBase: newKB }));
    };

    if (loading) return <div className="p-5 text-center">Loading...</div>;
    if (error) return <div className="p-5 text-center text-danger">{error}</div>;

    return (
        <div className="container-fluid py-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2 className="mb-0">
                    <Link to="/admin/brands" className="text-decoration-none text-muted me-2">Brands /</Link>
                    {brand.name}
                </h2>
                <Link to={`/admin/brands/${id}/inventory`} className="btn btn-outline-primary">
                    <i className="bi bi-box-seam me-2"></i>
                    Manage Inventory
                </Link>
            </div>

            {/* Analytics Section */}
            {analytics && (
                <div className="row g-4 mb-4">
                    <div className="col-md-3">
                        <div className="card border-0 shadow-sm">
                            <div className="card-body">
                                <h6 className="text-muted">Total Messages</h6>
                                <h3>{analytics.totalMessages}</h3>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="card border-0 shadow-sm">
                            <div className="card-body">
                                <h6 className="text-muted">Active Conversations</h6>
                                <h3>{analytics.activeConversations}</h3>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="card border-0 shadow-sm">
                            <div className="card-body">
                                <h6 className="text-muted">SMS Chats</h6>
                                <h3>{analytics.smsCount}</h3>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="card border-0 shadow-sm">
                            <div className="card-body">
                                <h6 className="text-muted">Web Chats</h6>
                                <h3>{analytics.webCount}</h3>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="row">
                {/* Brand Details Form */}
                <div className="col-lg-8">
                    <div className="card border-0 shadow-sm mb-4">
                        <div className="card-header bg-white">
                            <h5 className="mb-0">Brand Details</h5>
                        </div>
                        <div className="card-body">
                            <form onSubmit={handleSubmit}>
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label className="form-label">Brand Name</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Industry</label>
                                        <select
                                            className="form-select"
                                            name="industry"
                                            value={formData.industry}
                                            onChange={handleChange}
                                            required
                                        >
                                            <option value="">Select...</option>
                                            <option value="retail">Retail</option>
                                            <option value="government">Government</option>
                                            <option value="hospitality">Hospitality</option>
                                            <option value="healthcare">Healthcare</option>
                                            <option value="services">Services</option>
                                            <option value="automotive">Automotive</option>
                                            <option value="real_estate">Real Estate</option>
                                            <option value="home_services">Home Services</option>
                                            <option value="other">Other</option>
                                        </select>
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label">Phone</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Twilio Number</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="twilioNumber"
                                            value={formData.twilioNumber}
                                            onChange={handleChange}
                                            placeholder="+19029030090"
                                        />
                                        <div className="form-text">
                                            Dedicated inbound routing number (E.164, e.g. +19029030090). Leave blank for legacy
                                            brands routed by Phone.
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Email</label>
                                        <input
                                            type="email"
                                            className="form-control"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    <div className="col-12">
                                        <label className="form-label">Website</label>
                                        <input
                                            type="url"
                                            className="form-control"
                                            name="website"
                                            value={formData.website}
                                            onChange={handleChange}
                                            placeholder="https://"
                                        />
                                    </div>
                                    <div className="col-12">
                                        <label className="form-label">Description</label>
                                        <textarea
                                            className="form-control"
                                            name="description"
                                            rows="3"
                                            value={formData.description}
                                            onChange={handleChange}
                                        ></textarea>
                                    </div>

                                    <h6 className="mt-4 mb-2">Location</h6>
                                    <div className="col-12">
                                        <label className="form-label">Street Address</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="address.street"
                                            value={formData.address.street}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    <div className="col-md-5">
                                        <label className="form-label">City</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="address.city"
                                            value={formData.address.city}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">State</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="address.state"
                                            value={formData.address.state}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label">Zip Code</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="address.zipCode"
                                            value={formData.address.zipCode}
                                            onChange={handleChange}
                                        />
                                    </div>

                                    <div className="col-12">
                                        <h6 className="mt-4 mb-2">Hours of Operation</h6>
                                        <div className="form-text mb-2">
                                            Included in the AI assistant&apos;s knowledge. Leave a day&apos;s times blank if unknown, or mark it Closed.
                                        </div>
                                        {DAYS.map(day => (
                                            <div className="d-flex align-items-center gap-2 mb-2" key={day}>
                                                <div className="text-capitalize" style={{ width: '100px' }}>{day}</div>
                                                <input
                                                    type="time"
                                                    className="form-control form-control-sm"
                                                    style={{ maxWidth: '130px' }}
                                                    value={formData.hours[day].open}
                                                    disabled={formData.hours[day].closed}
                                                    onChange={(e) => handleHoursChange(day, 'open', e.target.value)}
                                                />
                                                <span className="text-muted">to</span>
                                                <input
                                                    type="time"
                                                    className="form-control form-control-sm"
                                                    style={{ maxWidth: '130px' }}
                                                    value={formData.hours[day].close}
                                                    disabled={formData.hours[day].closed}
                                                    onChange={(e) => handleHoursChange(day, 'close', e.target.value)}
                                                />
                                                <div className="form-check ms-2">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        id={`closed-${day}`}
                                                        checked={formData.hours[day].closed}
                                                        onChange={(e) => handleHoursChange(day, 'closed', e.target.checked)}
                                                    />
                                                    <label className="form-check-label" htmlFor={`closed-${day}`}>Closed</label>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="col-12">
                                        <h6 className="mt-4 mb-2">AI Personality (Chat & SMS)</h6>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Tone</label>
                                        <select
                                            className="form-select"
                                            name="aiPersonality.tone"
                                            value={formData.aiPersonality.tone}
                                            onChange={handleChange}
                                        >
                                            <option value="professional">Professional</option>
                                            <option value="friendly">Friendly</option>
                                            <option value="casual">Casual</option>
                                            <option value="formal">Formal</option>
                                        </select>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Greeting</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="aiPersonality.greeting"
                                            value={formData.aiPersonality.greeting}
                                            onChange={handleChange}
                                            placeholder="Hello! How can I help you today?"
                                        />
                                        <div className="form-text">
                                            First message shown when a chat or SMS conversation starts.
                                        </div>
                                    </div>
                                    <div className="col-12">
                                        <h6 className="mt-4 mb-2">Voice (Phone AI)</h6>
                                    </div>
                                    <div className="col-12">
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="voiceEnabled"
                                                checked={formData.voice.enabled}
                                                onChange={handleVoiceEnabledChange}
                                            />
                                            <label className="form-check-label" htmlFor="voiceEnabled">Enable voice channel</label>
                                        </div>
                                    </div>
                                    <div className="col-12">
                                        <label className="form-label">Greeting</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="voice.greeting"
                                            value={formData.voice.greeting}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">TTS Provider</label>
                                        <select
                                            className="form-select"
                                            name="voice.ttsProvider"
                                            value={formData.voice.ttsProvider}
                                            onChange={handleChange}
                                        >
                                            <option value="Google">Google</option>
                                        </select>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Voice ID</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="voice.voiceId"
                                            value={formData.voice.voiceId}
                                            onChange={handleChange}
                                        />
                                        <div className="form-text">
                                            Google TTS voice ID, e.g. en-US-Journey-O (English) or fr-CA-Neural2-A (French). Avoid fr-CA-Chirp-HD-O — rejected by Twilio (error 64101).
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Barge-in (interruptions)</label>
                                        <select
                                            className="form-select"
                                            name="voice.interruptible"
                                            value={formData.voice.interruptible}
                                            onChange={handleChange}
                                        >
                                            <option value="dtmf">Keypad only (recommended)</option>
                                            <option value="none">Never interrupt</option>
                                            <option value="speech">Speech only</option>
                                            <option value="any">Speech or keypad</option>
                                        </select>
                                        <div className="form-text">
                                            Controls what cuts off the AI mid-reply. "Keypad only" stops a caller's side-conversation or background speech from hijacking a turn. "Speech or keypad" is the most responsive but most easily triggered by stray talk.
                                        </div>
                                    </div>
                                    <div className="col-12">
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="voiceBilingual"
                                                checked={formData.voice.languages.includes('fr-CA')}
                                                onChange={handleBilingualChange}
                                            />
                                            <label className="form-check-label" htmlFor="voiceBilingual">Bilingual (English + French)</label>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Fallback Number</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="voice.fallbackNumber"
                                            value={formData.voice.fallbackNumber}
                                            onChange={handleChange}
                                            placeholder="+19029030090"
                                        />
                                        <div className="form-text">
                                            E.164 format (e.g. +19029030090), or leave blank.
                                        </div>
                                    </div>

                                </div>

                                <div className="mt-4 text-end">
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={saving}
                                    >
                                        {saving ? 'Saving...' : 'Save Changes'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>

                {/* Inventory Summary Sidebar */}
                <div className="col-lg-4">
                    <div className="card border-0 shadow-sm">
                        <div className="card-header bg-white d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">Inventory</h5>
                            <Link to={`/admin/brands/${id}/inventory`} className="btn btn-sm btn-outline-primary">
                                Manage
                            </Link>
                        </div>
                        <div className="card-body p-0">
                            {inventoryCategories.length === 0 ? (
                                <div className="text-center p-4 text-muted">
                                    <p className="mb-0">No inventory categories found.</p>
                                </div>
                            ) : (
                                <ul className="list-group list-group-flush">
                                    {inventoryCategories.map(cat => (
                                        <li key={cat._id} className="list-group-item d-flex justify-content-between align-items-center">
                                            <div>
                                                <strong>{cat.categoryName}</strong>
                                                <div className="small text-muted">{cat.items?.length || 0} items</div>
                                            </div>
                                            <span className="badge bg-light text-dark rounded-pill">
                                                {cat.isActive ? 'Active' : 'Inactive'}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Knowledge Base Section */}
            <div className="row mt-4">
                <div className="col-12">
                    <div className="card border-0 shadow-sm">
                        <div className="card-header bg-white d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">Knowledge Base (Q&A)</h5>
                            <button type="button" className="btn btn-sm btn-primary" onClick={addKBItem}>
                                <i className="bi bi-plus-lg me-1"></i> Add Question
                            </button>
                        </div>
                        <div className="card-body">
                            {formData.knowledgeBase.length === 0 ? (
                                <div className="text-center py-5 text-muted">
                                    <i className="bi bi-question-circle" style={{ fontSize: '3rem', opacity: 0.3 }}></i>
                                    <p className="mt-3 mb-0">No custom Q&A pairs added yet.</p>
                                    <p className="small">Add questions and answers to train the AI assistant.</p>
                                </div>
                            ) : (
                                <div className="d-flex flex-column gap-3">
                                    {formData.knowledgeBase.map((item, index) => (
                                        <div key={index} className="card bg-light border-0 p-3">
                                            <div className="d-flex justify-content-between mb-2">
                                                <strong className="text-muted small">Question #{index + 1}</strong>
                                                <button
                                                    type="button"
                                                    className="btn btn-link text-danger p-0 small text-decoration-none"
                                                    onClick={() => removeKBItem(index)}
                                                >
                                                    <i className="bi bi-trash me-1"></i>
                                                    Remove
                                                </button>
                                            </div>
                                            <div className="mb-2">
                                                <input
                                                    type="text"
                                                    className="form-control form-control-sm"
                                                    placeholder="Question (e.g. What is your return policy?)"
                                                    value={item.question}
                                                    onChange={(e) => handleKBChange(index, 'question', e.target.value)}
                                                />
                                            </div>
                                            <div>
                                                <textarea
                                                    className="form-control form-control-sm"
                                                    placeholder="Answer"
                                                    rows="2"
                                                    value={item.answer}
                                                    onChange={(e) => handleKBChange(index, 'answer', e.target.value)}
                                                ></textarea>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="mt-4 text-end">
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleSubmit}
                                    disabled={saving}
                                >
                                    {saving ? 'Saving...' : 'Save Knowledge Base'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Brand;
